import React, { useMemo } from 'react'
import dayjs from 'dayjs'
import { Box, Paper, Stack, Typography, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import parseJsonSafe from './parseJsonSafe'
import DateFormat from '../../store/types/enums/DateFormat'
import { History } from '../../store/redux/reducers/auditSlice'

interface ExpandableHistoryRowProps {
  data: History
}

const ExpandableHistoryRow: React.FC<ExpandableHistoryRowProps> = ({ data }) => {
  const { t } = useTranslation()
  const theme = useTheme()

  const oldDates = useMemo(() => parseJsonSafe(data.oldValue), [data.oldValue])
  const newDates = useMemo(() => parseJsonSafe(data.newValue), [data.newValue])

  const isAddition = Object.keys(oldDates).length === 0 && Object.keys(newDates).length > 0
  const isDeletion = Object.keys(oldDates).length > 0 && Object.keys(newDates).length === 0

  const actionLabel = t(`page.modification.${isAddition ? 'addition' : isDeletion ? 'deletion' : 'modification'}`)
  const actionColor = isAddition
    ? theme.palette.success.main
    : isDeletion
    ? theme.palette.error.main
    : theme.palette.info.main
  const [firstName, lastName] = data.name.split(' ')

  const dataValue = Object.keys(newDates).length
    ? dayjs(newDates.start_time).format('YYYY-MM-DD')
    : dayjs(oldDates.start_time).format('YYYY-MM-DD')

  const formatTimeRange = (start: string, end: string) =>
    `${dayjs(start).format('HH:mm')} - ${dayjs(end).format('HH:mm')}`

  let rangeValue
  if (Object.keys(oldDates).length === 0) {
    rangeValue = formatTimeRange(newDates.start_time, newDates.end_time)
  } else if (Object.keys(newDates).length === 0) {
    rangeValue = formatTimeRange(oldDates.start_time, oldDates.end_time)
  } else {
    rangeValue = `${formatTimeRange(oldDates.start_time, oldDates.end_time)} -> ${formatTimeRange(
      newDates.start_time,
      newDates.end_time
    )}`
  }

  return (
    <Paper
      sx={{
        borderRadius: 1,
        backgroundColor: 'background.elevation2',
      }}
      elevation={0}
    >
      <Box display="flex" flexWrap="wrap" flexDirection="column" sx={{ p: 1.5, mb: 2 }}>
        <Box display="flex" gap="10px" alignItems="center" justifyContent="space-between">
          <Stack direction="column" spacing="1px" sx={{ alignItems: 'flex-start', flex: 1, minWidth: 0 }}>
            <Typography
              variant="body1"
              sx={{
                wordBreak: 'break-word',
                whiteSpace: 'normal',
                overflowWrap: 'break-word',
              }}
            >
              {`${firstName} ${lastName}`}
            </Typography>
          </Stack>

          <Stack direction="row" gap={0.5} sx={{ flexShrink: 0 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 'bold',
                color: actionColor,
              }}
            >
              {actionLabel}
            </Typography>
            <Typography variant="body2">{dayjs(data.createdAt).format(DateFormat.HourMinute)}</Typography>
          </Stack>
        </Box>

        <Box sx={{ bgColor: 'background.elevation8', mt: 1.5, width: '100%' }}>
          <Stack direction="column" spacing={1} width="100%">
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                {t('page.schedule.date')}
              </Typography>
              <Typography variant="body2">{dataValue}</Typography>
            </Stack>

            <Stack direction="row" justifyContent="space-between" gap={0.5}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                {t('page.schedule.timeRange')}
              </Typography>
              <Typography variant="body2">{rangeValue}</Typography>
            </Stack>
          </Stack>
        </Box>
      </Box>
    </Paper>
  )
}

export default ExpandableHistoryRow